import { TrendingUp } from "lucide-react";
import { cn } from "@/lib/utils";

export type ChangeBaseline = "previous" | "yearly";
export type ChangeMode = "absolute" | "change";

type ExplorerChangeToggleProps = {
  changeMode: ChangeMode;
  baseline: ChangeBaseline;
  onModeChange: (mode: ChangeMode) => void;
  onBaselineChange: (baseline: ChangeBaseline) => void;
  className?: string;
};

/**
 * Segmented toggle for switching the map between absolute values and a change
 * view (current period minus a baseline). When the change view is active, a
 * second control picks whether the baseline is the immediately previous period
 * or the period closest to one year earlier.
 */
export function ExplorerChangeToggle({
  changeMode,
  baseline,
  onModeChange,
  onBaselineChange,
  className,
}: ExplorerChangeToggleProps) {
  return (
    <div className={cn("space-y-2 font-sans", className)}>
      <div className='grid grid-cols-2 gap-1 rounded-md border border-border bg-muted/40 p-1'>
        <button
          type='button'
          onClick={() => {
            onModeChange("absolute");
          }}
          className={cn(
            "cursor-pointer rounded-sm px-2 py-1 text-sm font-semibold transition-colors",
            changeMode === "absolute"
              ? "bg-card text-primary shadow-sm"
              : "text-muted-foreground hover:bg-muted hover:text-foreground",
          )}>
          Value
        </button>
        <button
          type='button'
          onClick={() => {
            onModeChange("change");
          }}
          className={cn(
            "flex cursor-pointer items-center justify-center gap-1 rounded-sm px-2 py-1 text-sm font-semibold transition-colors",
            changeMode === "change"
              ? "bg-card text-primary shadow-sm"
              : "text-muted-foreground hover:bg-muted hover:text-foreground",
          )}>
          <TrendingUp className='size-3.5' />
          Change
        </button>
      </div>

      {changeMode === "change" && (
        <div className='grid grid-cols-2 gap-1 rounded-md border border-border bg-muted/40 p-1'>
          <button
            type='button'
            onClick={() => {
              onBaselineChange("previous");
            }}
            className={cn(
              "cursor-pointer rounded-sm px-2 py-1 text-sm font-semibold transition-colors",
              baseline === "previous"
                ? "bg-card text-primary shadow-sm"
                : "text-muted-foreground hover:bg-muted hover:text-foreground",
            )}>
            Previous period
          </button>
          <button
            type='button'
            onClick={() => {
              onBaselineChange("yearly");
            }}
            className={cn(
              "cursor-pointer rounded-sm px-2 py-1 text-sm font-semibold transition-colors",
              baseline === "yearly"
                ? "bg-card text-primary shadow-sm"
                : "text-muted-foreground hover:bg-muted hover:text-foreground",
            )}>
            Previous year
          </button>
        </div>
      )}
    </div>
  );
}
